import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import apiService from '../services/apiService';
import { toast } from 'react-hot-toast';
import { Clock, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { useBranding } from '../context/BrandingContext';
import './PublicCalendarWidget.css';

// [260928-v4n] Статус — только из поля booking ответа (бэкенд ставит его на каждом пути).
// Отсутствует/неизвестен → 'request_only': «забронировано» без подтверждения сервера не говорим.
const IZVESTNYE = ['scheduled', 'request_only', 'failed'];
export const statusIzOtveta = (otvet) => {
    const s = otvet && otvet.booking && otvet.booking.status;
    return IZVESTNYE.includes(s) ? s : 'request_only';
};

// Строка времени → Date; пусто или не разбирается → null.
const dataIzStroki = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
};

// [261005-2fy] Страница сайта, с которой открыт календарь (поп-ап передаёт ?page=). Пусто — не встроен.
const stranicaRoditelya = () => {
    try { return (new URLSearchParams(window.location.search).get('page') || '').slice(0, 300); }
    catch (e) { return ''; }
};

// [261005-2fy] Текст галочки — с сервера (один держатель); подписи ссылок внутри текста становятся ссылками.
export const tekstSoSsylkami = (text, links) => {
    const chasti = [String(text || '')];
    (links || []).forEach(({ label, url }) => {
        for (let i = 0; i < chasti.length; i += 1) {
            if (typeof chasti[i] !== 'string') continue;
            const pos = chasti[i].indexOf(label);
            if (pos < 0) continue;
            const a = <a key={label} href={url} target="_blank" rel="noopener noreferrer">{label}</a>;
            chasti.splice(i, 1, chasti[i].slice(0, pos), a, chasti[i].slice(pos + label.length));
            break;
        }
    });
    return chasti;
};

// [261005-2fy] Сообщить сайту-родителю об отправке записи (для GA). Без личных данных; только разрешённому
// сервером адресу (parent_origins) и только если календарь действительно встроен.
export const soobshchitRoditelyu = (status, stranica, razreshyonnye) => {
    try {
        if (!stranica || window.parent === window || !Array.isArray(razreshyonnye)) return false;
        const origin = new URL(stranica).origin;
        if (!razreshyonnye.includes(origin)) return false;
        window.parent.postMessage({ source: 'legal-os-calendar', event: 'booking_submitted', status }, origin);
        return true;
    } catch (e) { return false; }
};

const PublicCalendarWidget = () => {
    // eslint-disable-next-line no-unused-vars
    const { branding, isFitenko } = useBranding();
    // [PROPOSED 09-07] Адрес встречи — из данных арендатора (/api/v1/branding → company_profiles.address),
    // не литералом: виджет общий для всех арендаторов. Пусто → вариант без адреса.
    const adresOfisa = (branding && typeof branding.address === 'string' && branding.address.trim()) || null;
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [booking, setBooking] = useState(null); // Selected slot to book
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        topic: 'consultation',
        consultationType: 'Phone', // [NEW] Default
        city: '', // [NEW]
        issueDescription: '' // [NEW]
    });
    const [step, setStep] = useState('date'); // 'date' | 'form' | 'success'
    // [260928-v4n] Что сервер сделал с временем: 'scheduled' | 'request_only' | 'failed'; и время события.
    const [statusBroni, setStatusBroni] = useState('request_only');
    // [261005-2fy] SMS-согласие: текст приходит с сервера (null → галочки нет); по умолчанию НЕ отмечена.
    const [soglasieInfo, setSoglasieInfo] = useState(null);
    const [smsSoglasie, setSmsSoglasie] = useState(false);
    useEffect(() => {
        let zhiv = true;
        // Любой сбой (в т.ч. синхронный) — в .catch: текст согласия не должен ломать календарь, он путь заявки.
        Promise.resolve()
            .then(() => apiService.getSmsConsentText())
            .then(o => { if (zhiv && o && typeof o.text === 'string' && o.text) setSoglasieInfo(o); })
            .catch(() => { /* 404 у арендатора без галочки — это норма */ });
        return () => { zhiv = false; };
    }, []);
    const [nachaloBroni, setNachaloBroni] = useState(null);

    const fetchSlots = async (date) => {
        setLoading(true);
        try {
            const dateStr = format(date, 'yyyy-MM-dd');
            const freeSlots = await apiService.getFreeSlots(dateStr);
            setSlots(freeSlots);
        } catch (error) {
            console.error("Failed to fetch slots:", error);
            // toast.error("Could not load available times.");
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch when date changes
    React.useEffect(() => {
        if (selectedDate) {
            fetchSlots(selectedDate);
        }
    }, [selectedDate]);

    // [New] Theming Support (Wave 3 — RESEARCH Pitfall 5 gate)
    // The widget supports an embeddable `?primaryColor=ff0000` query-param override
    // for 3rd-party hosts. On tenant-themed deployments (Fitenko OS), we must NOT
    // let an absent query param clobber the tenant palette via setProperty(undefined).
    // Gate: only apply override when primaryColor is explicitly provided AND the
    // document is not already Fitenko-themed (Fitenko tenant theming takes precedence).
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const primaryColor = params.get('primaryColor');
        const isFitenkoTenant = document.documentElement.classList.contains('fitenko');
        if (primaryColor && !isFitenkoTenant) {
            document.documentElement.style.setProperty('--primary-color', `#${primaryColor}`);
            // Also set derived colors if needed, or use CSS calc/opacity
            document.documentElement.style.setProperty('--primary-hover', `#${primaryColor}dd`);
        }
        // [NEW] Opt-in dark theme for embeds hosted on dark-themed sites (e.g. estatelawbrooklyn.com).
        // Toggled via ?theme=dark; styling lives in the `html.embed-dark` block of the CSS.
        // Additive + gated → MLG/Fitenko embeds (no theme param) keep their light palette.
        if (params.get('theme') === 'dark') {
            document.documentElement.classList.add('embed-dark');
        }
    }, []);

    const handleSlotClick = (slot) => {
        setBooking(slot);
        setStep('form');
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Simplified Lead Creation (backend handles booking ideally, or we note it)
            // [NEW] Construct detailed notes from new fields
            const detailedNotes = `
Appointment Request: ${format(new Date(booking), 'Pp')}
Type: ${formData.consultationType} Consultation
City: ${formData.city}
Issue: ${formData.issueDescription}
            `.trim();

            const payload = {
                ...formData,
                service: 'Calendar Booking',
                // [260928-v4n] Прежний заголовок обещал бесплатную консультацию — уходил в notes лида и лгал для платной.
                topic: 'Consultation Request',
                source: 'Widget Calendar',
                language: 'en',
                appointment_datetime: booking,
                notes: detailedNotes,
                // [261005-2fy] true только если галочка показана И отмечена посетителем
                sms_consent: Boolean(soglasieInfo && smsSoglasie),
                page: stranicaRoditelya() || undefined
            };

            const otvet = await apiService.createLead(payload);
            const status = statusIzOtveta(otvet);
            setStatusBroni(status);
            setNachaloBroni(status === 'scheduled' ? ((otvet && otvet.booking && otvet.booking.start) || null) : null);
            setStep('success');
            soobshchitRoditelyu(status, stranicaRoditelya(), soglasieInfo && soglasieInfo.parent_origins);

        } catch (error) {
            console.error(error);
            toast.error("Your request could not be sent. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (step === 'success') {
        // [260928-v4n] Три текста по статусу сервера. «Booked» — только при 'scheduled'.
        const zabronirovano = statusBroni === 'scheduled';
        const dataSobytiya = dataIzStroki(nachaloBroni) || dataIzStroki(booking);
        const dataZaprosa = dataIzStroki(booking);
        return (
            <div className="mlg-calendar-container success-view">
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    {zabronirovano && <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>}
                    <h3 style={{ color: 'var(--dark-blue)', marginBottom: '8px' }}>{zabronirovano ? "You're booked!" : 'Request Sent!'}</h3>
                    {zabronirovano ? (
                        <p style={{ color: 'var(--secondary-blue)', lineHeight: '1.5' }}>
                            Your consultation is booked for{' '}
                            <strong style={{ color: 'var(--primary-blue)' }}>{dataSobytiya ? format(dataSobytiya, 'PPPP p') : ''}</strong>. A confirmation will follow.
                        </p>
                    ) : (
                        <p style={{ color: 'var(--secondary-blue)', lineHeight: '1.5' }}>
                            {statusBroni === 'failed'
                                ? 'We received your request but could not reserve the time automatically — the office will contact you to confirm.'
                                : 'Thank you — the office will contact you to confirm the time.'}
                        </p>
                    )}
                    {!zabronirovano && dataZaprosa && (
                        <p style={{ color: 'var(--secondary-blue)', fontSize: '0.9rem', marginTop: '8px' }}>
                            Preferred time: <strong style={{ color: 'var(--primary-blue)' }}>{format(dataZaprosa, 'PPPP p')}</strong>
                        </p>
                    )}
                    <button onClick={() => setStep('date')} className="mlg-btn-primary" style={{ marginTop: '24px' }}>
                        Choose Another Time
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'form') {
        return (
            <div className="mlg-calendar-container form-view">
                <button className="mlg-back-btn" onClick={() => setStep('date')}>
                    <ChevronLeft size={16} /> Back
                </button>

                <h3 className="mlg-section-title">Enter Details</h3>
                <p className="mlg-selected-time">
                    {format(new Date(booking), 'PPPP')} at {format(new Date(booking), 'p')}
                </p>

                <form onSubmit={handleFormSubmit} className="mlg-booking-form">
                    <div className="form-group">
                        <label>Consultation Format *</label>
                        <select
                            required
                            value={formData.consultationType}
                            onChange={e => setFormData({ ...formData, consultationType: e.target.value })}
                            style={{
                                padding: '10px 12px',
                                border: '1px solid var(--border)',
                                borderRadius: '6px',
                                fontSize: '0.95rem',
                                background: 'var(--popover, white)'
                            }}
                        >
                            <option value="In-Person">{adresOfisa ? `In-Person Consultation (${adresOfisa})` : 'In-Person Consultation'}</option>
                            <option value="Phone">Phone Consultation</option>
                            <option value="Video">Video Consultation (Zoom/Google Meet)</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Full Name *</label>
                        <input
                            type="text"
                            required
                            value={formData.fullName}
                            onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label>Email *</label>
                        <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label>Phone Number *</label>
                        <input
                            type="tel"
                            required
                            value={formData.phone}
                            onChange={e => setFormData({ ...formData, phone: e.target.value })}
                        />
                    </div>

                    <div className="form-group">
                        <label>What city do you live in? *</label>
                        <input
                            type="text"
                            required
                            placeholder={isFitenko ? "e.g. New York, Miami" : "e.g. New York, Manhattan, Brooklyn"}
                            value={formData.city}
                            onChange={e => setFormData({ ...formData, city: e.target.value })}
                        />
                    </div>

                    <div className="form-group">
                        <label>Brief description of your legal issue *</label>
                        <textarea
                            required
                            rows={3}
                            placeholder="Please briefly describe how we can help..."
                            value={formData.issueDescription}
                            onChange={e => setFormData({ ...formData, issueDescription: e.target.value })}
                            style={{
                                padding: '10px 12px',
                                border: '1px solid var(--border)',
                                borderRadius: '6px',
                                fontSize: '0.95rem',
                                fontFamily: 'inherit',
                                resize: 'vertical'
                            }}
                        />
                    </div>

                    {soglasieInfo && (
                        <div className="form-group">
                            <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: '0.8rem', lineHeight: 1.45 }}>
                                <input
                                    type="checkbox"
                                    checked={smsSoglasie}
                                    onChange={e => setSmsSoglasie(e.target.checked)}
                                    style={{ marginTop: '3px', flexShrink: 0 }}
                                />
                                <span>{tekstSoSsylkami(soglasieInfo.text, soglasieInfo.links)}</span>
                            </label>
                        </div>
                    )}

                    <button type="submit" className="mlg-btn-primary" disabled={loading}>
                        {loading ? 'Sending...' : 'Request This Time'}
                    </button>
                </form>
            </div>
        );
    }

    // [NEW] Disable weekends (Sunday=0, Saturday=6)
    const isWeekday = (date) => {
        const day = date.getDay();
        return day !== 0 && day !== 6;
    };

    return (
        <div className="mlg-calendar-container">
            {/* Header Removed as per user request */}

            {/* Calendar Section */}
            <div className="mlg-calendar-section">
                <h3 className="mlg-section-title">Select a Date & Time</h3>

                <div className="mlg-datepicker-wrapper">
                    <DatePicker
                        selected={selectedDate}
                        onChange={date => setSelectedDate(date)}
                        minDate={new Date()}
                        filterDate={isWeekday} // [NEW] Only allow Mon-Fri
                        inline
                        calendarClassName="mlg-custom-calendar"
                        dayClassName={date => "mlg-custom-day"}
                        renderCustomHeader={({
                            date,
                            decreaseMonth,
                            increaseMonth,
                            prevMonthButtonDisabled,
                            nextMonthButtonDisabled,
                        }) => (
                            <div className="mlg-calendar-nav">
                                <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                                    <ChevronLeft size={20} />
                                </button>
                                <span className="current-month">
                                    {format(date, 'MMMM yyyy')}
                                </span>
                                <button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                        )}
                    />
                </div>
            </div>

            {/* Slots Section */}
            <div className="mlg-slots-section">
                {loading ? (
                    <div className="mlg-loading">Loading slots...</div>
                ) : (
                    <div className="mlg-slots-grid">
                        {slots.length > 0 ? slots.map((slot, i) => (
                            <button
                                key={i}
                                className="mlg-slot-btn"
                                onClick={() => handleSlotClick(slot)}
                            >
                                {format(new Date(slot), 'h:mm aa')}
                            </button>
                        )) : (
                            <div className="mlg-no-slots">
                                No times available for {format(selectedDate, 'MMM d')}.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PublicCalendarWidget;
